import { useMutation, useQueryClient } from '@tanstack/react-query';

import axios from '@/portainer/services/axios';
import { withError } from '@/react-tools/react-query';

import { SilenceData } from './silenceTypes';

export interface CreateSilencePayload {
  alertManagerURL: string;
  silence: {
    comment: string;
    createdBy: string;
    startsAt: string;
    endsAt: string;
    matchers: Array<{
      name: string;
      value: string;
      isRegex: boolean;
      isEqual?: boolean;
    }>;
  };
}

export interface CreateSilenceResponse {
  message: string;
  silence: {
    id: string;
    comment: string;
    createdBy: string;
    startsAt: string;
    endsAt: string;
    matchers: Array<{
      name: string;
      value: string;
      isRegex: boolean;
      isEqual?: boolean;
    }>;
  };
}

export async function createSilence(
  silenceData: SilenceData,
  alertManagerURL: string = 'localhost:9093'
): Promise<CreateSilenceResponse> {
  // Convert datetime-local strings to ISO format for the backend
  // Add a small buffer to ensure times are not in the past due to network delays
  const now = new Date();
  const startTime = new Date(silenceData.startsAt);
  const endTime = new Date(silenceData.endsAt);

  // Ensure start time is not in the past (add 1 second buffer)
  const adjustedStartTime =
    startTime < now ? new Date(now.getTime() + 1000) : startTime;

  // Ensure end time is after start time (add 1 minute minimum if needed)
  const adjustedEndTime =
    endTime <= adjustedStartTime
      ? new Date(adjustedStartTime.getTime() + 60 * 1000)
      : endTime;

  const startsAt = adjustedStartTime.toISOString();
  const endsAt = adjustedEndTime.toISOString();

  const payload: CreateSilencePayload = {
    alertManagerURL,
    silence: {
      comment: silenceData.comment,
      createdBy: silenceData.createdBy,
      startsAt,
      endsAt,
      matchers: silenceData.matchers,
    },
  };

  const { data } = await axios.post<CreateSilenceResponse>(
    '/observability/alerts/silence',
    payload
  );

  return data;
}

export function useCreateSilence() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      silenceData,
      alertManagerURL,
    }: {
      silenceData: SilenceData;
      alertManagerURL?: string;
    }) => createSilence(silenceData, alertManagerURL),
    {
      onSuccess: () => {
        // Invalidate active alerts and silenced alerts queries to refresh the UI
        queryClient.invalidateQueries(['alerts', 'active']);
        queryClient.invalidateQueries(['alerts', 'silenced']);
      },
      ...withError('Failed to create alert silence'),
    }
  );
}
